/**
 * NotFound.tsx
 *
 * Purpose: Themed 404 page for Mythbound Adventure Studio™.
 * Created: 2026-01-22
 * Modified: 2026-01-22
 *
 * WHO: Users navigating to non-existent routes.
 * WHAT: Displays a themed 404 error page with navigation options.
 * WHERE: Mythbound.App/src/pages/NotFound.tsx
 * WHY: Provides a consistent, on-brand experience for missing pages.
 *
 * INTEGRATION POINTS:
 * - App.tsx: Used as the fallback route for unmatched paths
 * - Future pages: As features are implemented, routes will be added
 *   and this page will only appear for truly missing content
 *
 * DESIGN NOTES:
 * - Uses Mythbound theme colors and typography
 * - Matches AuthError.tsx styling for consistency
 * - Provides clear navigation back to Dashboard
 * - Adventure-themed messaging to stay on brand
 */

import React, { useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router';
import { theme as mythboundTheme } from '../config/theme';
import { GlobalFooter } from '../components/ui/GlobalFooter';

// =============================================================================
// FEATURE STATUS MAPPING
// Maps routes to their implementation status for helpful messaging
// Update this as features are implemented
// =============================================================================
const FEATURE_STATUS: Record<string, { name: string; status: 'planned' | 'coming_soon' | 'not_found' }> = {
  '/session/new': { name: 'Session Creation', status: 'planned' },
  '/character/new': { name: 'Character Creation', status: 'planned' },
  '/notes': { name: 'Player Notes', status: 'planned' },
  '/spellbook': { name: 'Spellbook', status: 'planned' },
  '/map': { name: 'Game Map', status: 'planned' },
  '/settings': { name: 'User Settings', status: 'planned' },
  '/voice': { name: 'Voice Chat', status: 'planned' },
};

// =============================================================================
// COMPONENT: NotFound
// =============================================================================

/**
 * NotFound Component
 *
 * WHO: Users who navigate to non-existent or not-yet-implemented routes.
 * WHAT: Renders a themed 404 page with helpful messaging and navigation.
 * WHERE: /any-unmatched-route in the React application.
 * WHY: Provides a friendly, on-brand experience for missing pages.
 *
 * ASYNC INTEGRATION:
 * - As new features are added, update FEATURE_STATUS mapping
 * - Add new routes to App.tsx as they become available
 * - This component will automatically adapt messaging based on the route
 */
export const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardButtonRef = useRef<HTMLButtonElement>(null);

  // ---------------------------------------------------------------------------
  // Determine if this is a known planned feature or truly unknown route
  // ---------------------------------------------------------------------------
  const featureInfo = FEATURE_STATUS[location.pathname];
  const isPlannedFeature = featureInfo?.status === 'planned' || featureInfo?.status === 'coming_soon';

  // ---------------------------------------------------------------------------
  // Auto-focus the primary action button for accessibility
  // ---------------------------------------------------------------------------
  useEffect(() => {
    dashboardButtonRef.current?.focus();
  }, []);

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------
  const handleDashboard = () => {
    navigate('/dashboard');
  };

  const handleBack = () => {
    // If there's history, go back; otherwise go to dashboard
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <Flex
        minHeight="100vh"
        width="full"
        align="center"
        justifyContent="center"
        bg={mythboundTheme.colors.gradients.darkPortal}
        px={4}
        py={12}
      >
        <Box
          maxW="520px"
          w="full"
          p={{ base: 6, md: 8 }}
          borderWidth={2}
          borderRadius="lg"
          boxShadow={mythboundTheme.shadows.glow}
          borderColor={mythboundTheme.colors.primary.main}
          bg="rgba(17, 17, 22, 0.9)"
        >
          <VStack spacing={6} align="stretch">
            {/* ===============================================================
                Icon Section - Compass/Search themed for "lost" messaging
            =============================================================== */}
            <Flex justify="center">
              <Box
                width="72px"
                height="72px"
                borderRadius="full"
                bg="rgba(59, 232, 255, 0.15)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow={mythboundTheme.shadows.glow}
              >
                <SearchIcon boxSize={8} color={mythboundTheme.colors.primary.main} aria-hidden="true" />
              </Box>
            </Flex>

            {/* ===============================================================
                Error Message Section
            =============================================================== */}
            <Box textAlign="center" role="alert" aria-live="polite">
              <Heading
                fontFamily={mythboundTheme.typography.fontFamily.heading}
                color={mythboundTheme.colors.primary.gold}
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={2}
              >
                {isPlannedFeature ? 'Quest Not Yet Available' : 'Path Not Found'}
              </Heading>
              <Text color={mythboundTheme.colors.accent.silver} fontSize="md">
                {isPlannedFeature
                  ? `The ${featureInfo.name} feature is being forged in the fires of development. Return to your dashboard while our artificers complete their work.`
                  : "The path you seek has vanished into the mists. Perhaps the map was misread, or the destination exists only in legend."}
              </Text>
            </Box>

            {/* ===============================================================
                Route Badge - Shows the attempted path
            =============================================================== */}
            <HStack justify="center">
              <Badge
                px={3}
                py={1}
                borderRadius="full"
                bg="rgba(255, 255, 255, 0.08)"
                color={mythboundTheme.colors.accent.silver}
                fontFamily="monospace"
                fontSize="sm"
              >
                {location.pathname}
              </Badge>
              {isPlannedFeature && (
                <Badge
                  px={3}
                  py={1}
                  borderRadius="full"
                  bg="rgba(217, 181, 85, 0.2)"
                  color={mythboundTheme.colors.primary.gold}
                  fontSize="xs"
                >
                  Coming Soon
                </Badge>
              )}
            </HStack>

            {/* ===============================================================
                Action Buttons
            =============================================================== */}
            <VStack spacing={3}>
              <Button
                ref={dashboardButtonRef}
                width="full"
                onClick={handleDashboard}
                bg={mythboundTheme.colors.primary.main}
                color={mythboundTheme.colors.primary.dark}
                fontWeight="bold"
                _hover={{
                  bg: 'rgba(59, 232, 255, 0.85)',
                  boxShadow: mythboundTheme.shadows.glow,
                }}
              >
                Return to Dashboard
              </Button>
              <Button
                width="full"
                variant="outline"
                borderColor={mythboundTheme.colors.accent.silver}
                color={mythboundTheme.colors.accent.silver}
                _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                onClick={handleBack}
              >
                Go Back
              </Button>
            </VStack>

            {/* ===============================================================
                404 Code Display - Subtle theming element
            =============================================================== */}
            <Box textAlign="center">
              <Text
                fontFamily={mythboundTheme.typography.fontFamily.heading}
                fontSize="6xl"
                fontWeight="bold"
                color="rgba(59, 232, 255, 0.15)"
                lineHeight="1"
                userSelect="none"
              >
                404
              </Text>
            </Box>
          </VStack>
        </Box>
      </Flex>
      <Box px={{ base: 4, md: 8 }} pb={6}>
        <GlobalFooter />
      </Box>
    </>
  );
};

export default NotFound;
